import AppTheme from '../theme';
import environment from '../config/environment';
import ErrorBoundary from '../components/error-boundary/ErrorBoundary';
import HeaperProvider from './providers/HeaperProvider';
import InitAuth from '../InitAuth';
import InitLD from '../InitLD';
import React from 'react';
import ReactDOM, { type Root } from 'react-dom/client';
import store, { persistor } from '../redux/store';
import { appInitiated } from '../redux/slice/appSlice';
import { Auth0Provider } from '@equipmentshare/auth0-react';
import { BrowserRouter } from 'react-router-dom';
import { DataAttributes } from '../types/attributes';
import { initSiteLevelDate } from '../utils/cart';
import { IntlProvider } from 'react-intl';
import { LD_BOOTSTRAP_CONTEXT } from '@app/constants/ld';
import { createLDReactProvider } from '@launchdarkly/react-sdk';
import { locale, messages } from '../locales';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider as ReduxProvider } from 'react-redux';
import { RendererComponent, RendererComponentType, RendererSelectorType } from '../types/renderer';

const LDProvider = createLDReactProvider(
  environment.LD_CLIENT_SIDE_ID,
  LD_BOOTSTRAP_CONTEXT,
  {
    ldOptions: {
      streaming: false,
      sendEvents: true,
      useCamelCaseFlagKeys: false,
    },
  },
);

const onBeforeLift = () => {
  if (!store.getState().app.appInitiated) {
    store.dispatch(appInitiated());
    initSiteLevelDate();
  }
};

// `renderer()` may run more than once for the same host node (e.g. Strict Mode in Development).
// React 18 allows only one `createRoot` per container; reuse the root and call `render` to update.
const elementToReactRoot = new WeakMap<HTMLElement, Root>();

const renderElement = (
  element: HTMLElement,
  component: React.FC<DataAttributes>,
  componentType: RendererComponentType,
) => {
  const attributes = Object.assign(
    {},
    ...Array.from(element.attributes, ({ name, value }) => ({ [name]: value })),
  );
  let domRoot = elementToReactRoot.get(element);
  if (!domRoot) {
    domRoot = ReactDOM.createRoot(element);
    elementToReactRoot.set(element, domRoot);
  }

  domRoot.render(
    <React.StrictMode>
      <ErrorBoundary>
        <ReduxProvider store={store}>
          <PersistGate persistor={persistor} onBeforeLift={onBeforeLift}>
            <IntlProvider locale={locale} messages={messages}>
              <AppTheme>
                <BrowserRouter>
                  {componentType === RendererComponentType.AUTH ? (
                    <>
                      <Auth0Provider
                        clientId={environment.AUTH0_CONFIG.CLIENT_ID}
                        domain={environment.AUTH0_CONFIG.DOMAIN}
                        audience={environment.AUTH0_CONFIG.AUDIENCE}
                        redirectUri={window.location.origin}
                        scope='openid profile email offline_access'
                        cacheLocation='localstorage'
                        useRefreshTokens={true}
                        allowUnauthorizedAccess={true}
                      >
                        <InitAuth>
                          <HeaperProvider />
                          <LDProvider>
                            <InitLD />
                          </LDProvider>
                          {component(attributes)}
                        </InitAuth>
                      </Auth0Provider>
                    </>
                  ) : (
                    component(attributes)
                  )}
                </BrowserRouter>
              </AppTheme>
            </IntlProvider>
          </PersistGate>
        </ReduxProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
};

const renderer = (data: RendererComponent | RendererComponent[]) => {
  const startRendering = () => {
    (Array.isArray(data) ? data : [data]).forEach(
      ({ componentType, selectorType, selector, component }) => {
        if (selectorType === RendererSelectorType.ID) {
          const element = document.getElementById(selector) as HTMLElement;
          if (element) {
            renderElement(element, component, componentType);
          }
        }
        if (selectorType === RendererSelectorType.CLASS) {
          const elements = document.getElementsByClassName(selector);
          for (let i = 0; i < elements.length; i++) {
            const element = elements[i] as HTMLElement;
            renderElement(element, component, componentType);
          }
        }
        if (selectorType === RendererSelectorType.ATTRIBUTE) {
          const elements = document.querySelectorAll(selector);
          for (let i = 0; i < elements.length; i++) {
            const element = elements[i] as HTMLElement;
            renderElement(element, component, componentType);
          }
        }
      },
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startRendering, { once: true });
  } else {
    startRendering();
  }
};

export default renderer;
